import type {
  IntegrationConfig,
  IntegrationResult,
  IntegrationLogger,
  IntegrationError,
  IntegrationParams,
} from '../types';
import { ConsoleLogger } from './logger';
import { validateParams } from './validation';
import { withRetry } from './retry';

/**
 * Base class for all integrations
 */
export abstract class BaseIntegration {
  protected config: IntegrationConfig;
  protected logger: IntegrationLogger;

  constructor(config: IntegrationConfig) {
    this.config = config;
    this.logger = config.logger || new ConsoleLogger();
  }

  /**
   * Execute an action
   */
  abstract execute(
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult>;

  /**
   * Validate action params against registry
   */
  protected validateParams(
    action: string,
    params: IntegrationParams,
  ): ReturnType<typeof validateParams> {
    return validateParams(this.config.name, action, params);
  }

  /**
   * Handle errors uniformly
   */
  protected handleError(action: string, error: unknown): IntegrationResult {
    this.logger.error(`Integration error in ${this.config.name}.${action}`, {
      error: error instanceof Error ? error : new Error(String(error)),
    });

    const integrationError =
      error instanceof Error
        ? error
        : new Error(String(error));

    return {
      success: false,
      error: integrationError as IntegrationError,
      metadata: this.createMetadata(action, 0, 0),
    };
  }

  /**
   * Create metadata for result
   */
  protected createMetadata(
    action: string,
    duration: number,
    retries: number = 0,
  ): IntegrationResult['metadata'] {
    return {
      integration: this.config.name,
      action,
      duration,
      retries,
      timestamp: Date.now(),
    };
  }

  /**
   * Execute with retry logic
   */
  protected async executeWithRetry<T>(
    fn: () => Promise<T>,
  ): Promise<T> {
    if (!this.config.retry) {
      return fn();
    }

    return withRetry(fn, {
      maxAttempts: this.config.retry.maxAttempts,
      backoffMs: this.config.retry.backoffMs,
      maxBackoffMs: this.config.retry.maxBackoffMs,
      retryableErrors: [
        'TIMEOUT_ERROR',
        'NETWORK_ERROR',
        'RATE_LIMIT_ERROR',
      ],
    });
  }
}
